import type { Permission } from '@hostesa/auth-client';
import type { FeatureFlag } from '@hostesa/feature-flags';
import {
  Activity,
  Building2,
  DollarSign,
  Flag,
  Hotel,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  type LucideIcon,
  Phone,
  Server,
  ShieldCheck,
  TriangleAlert,
  UserPlus,
  Users,
} from 'lucide-react';

/**
 * Declarative backoffice (operator console) navigation. Each item is gated by an
 * RBAC permission and optionally a feature flag. Visibility here is a UX layer
 * only — the route guards (operator session + RBAC + flag) and the backend
 * remain authoritative.
 *
 * Groups mirror the operator's mental model:
 * Oversight · Operations · Platform · Diagnostics.
 *
 * NOTE: every cross-tenant route requires `admin:cross_tenant_read`. The
 * provider-diagnostics route additionally requires the
 * `backoffice_provider_diagnostics` flag — the ONLY place provider metadata may
 * appear.
 */

export type NavGroupId =
  | 'overview'
  | 'customers'
  | 'operations'
  | 'runtime'
  | 'providers'
  | 'finance'
  | 'governance'
  | 'provisioning';

export interface NavItemConfig {
  /** Stable id — also used for the data-testid and the route path segment. */
  id: string;
  /** Top-level path segment (operator routes are NOT slug-scoped). */
  segment: string;
  /** Lucide icon rendered before the label (prototype `.navitem .ic`). */
  icon: LucideIcon;
  /** i18n key under the `backoffice:nav` namespace. */
  labelKey: string;
  /** RBAC permission required to see/reach the item. */
  permission: Permission;
  /** Optional feature flag that must be enabled. */
  flag?: FeatureFlag;
}

export interface NavGroupConfig {
  id: NavGroupId;
  /** i18n key under `backoffice:navGroups`. */
  labelKey: string;
  items: NavItemConfig[];
}

// IA mirrors the product prototype: Overview · Customers · Operations · Runtime
// · Providers · Finance · Governance, with Provisioning kept last (a real
// operator feature the prototype omits). Per-item RBAC still gates visibility.
export const NAV_GROUPS: readonly NavGroupConfig[] = [
  {
    id: 'overview',
    labelKey: 'navGroups.overview',
    items: [
      {
        id: 'overview',
        segment: 'overview',
        icon: LayoutDashboard,
        labelKey: 'nav.overview',
        permission: 'admin:cross_tenant_read',
      },
    ],
  },
  {
    id: 'customers',
    labelKey: 'navGroups.customers',
    items: [
      {
        id: 'tenants',
        segment: 'tenants',
        icon: Building2,
        labelKey: 'nav.tenants',
        permission: 'admin:cross_tenant_read',
      },
      {
        id: 'hotels',
        segment: 'hotels',
        icon: Hotel,
        labelKey: 'nav.hotels',
        permission: 'admin:cross_tenant_read',
      },
      {
        id: 'users',
        segment: 'users',
        icon: Users,
        labelKey: 'nav.users',
        permission: 'admin:cross_tenant_read',
      },
      // Support === impersonation; gated on the impersonate permission.
      {
        id: 'support',
        segment: 'support',
        icon: LifeBuoy,
        labelKey: 'nav.support',
        permission: 'admin:impersonate',
      },
    ],
  },
  {
    id: 'operations',
    labelKey: 'navGroups.operations',
    items: [
      {
        id: 'calls',
        segment: 'calls',
        icon: Phone,
        labelKey: 'nav.calls',
        permission: 'admin:cross_tenant_read',
      },
      {
        id: 'incidents',
        segment: 'incidents',
        icon: TriangleAlert,
        labelKey: 'nav.incidents',
        permission: 'admin:cross_tenant_read',
      },
    ],
  },
  {
    id: 'runtime',
    labelKey: 'navGroups.runtime',
    items: [
      {
        id: 'queues',
        segment: 'queues',
        icon: Server,
        labelKey: 'nav.queues',
        permission: 'admin:cross_tenant_read',
      },
      {
        id: 'events',
        segment: 'events',
        icon: Activity,
        labelKey: 'nav.events',
        permission: 'admin:cross_tenant_read',
      },
    ],
  },
  {
    id: 'providers',
    labelKey: 'navGroups.providers',
    items: [
      {
        id: 'providers',
        segment: 'providers',
        icon: Layers,
        labelKey: 'nav.providers',
        permission: 'admin:cross_tenant_read',
        // Provider metadata is ONLY visible behind this flag.
        flag: 'backoffice_provider_diagnostics',
      },
    ],
  },
  {
    id: 'finance',
    labelKey: 'navGroups.finance',
    items: [
      {
        id: 'costs',
        segment: 'costs',
        icon: DollarSign,
        labelKey: 'nav.costs',
        permission: 'usage:read',
      },
    ],
  },
  {
    id: 'governance',
    labelKey: 'navGroups.governance',
    items: [
      {
        id: 'audit-log',
        segment: 'audit-log',
        icon: ShieldCheck,
        labelKey: 'nav.auditLog',
        permission: 'admin:cross_tenant_read',
      },
      {
        id: 'feature-flags',
        segment: 'feature-flags',
        icon: Flag,
        labelKey: 'nav.featureFlags',
        permission: 'admin:cross_tenant_read',
      },
    ],
  },
  {
    id: 'provisioning',
    labelKey: 'navGroups.provisioning',
    items: [
      {
        id: 'register',
        segment: 'register',
        icon: UserPlus,
        labelKey: 'nav.register',
        permission: 'admin:provision',
      },
    ],
  },
] as const;
